import { Group } from '@/types/types'
import {
    CalendarDays,
    Heart,
    House,
    LayoutDashboard,
    Search,
    Calendar,
    CalendarCheck,
    DollarSign,
    Bell,
    MessagesSquare
} from 'lucide-react'
import type { UserRole } from '@prisma/client'
import { Transition } from 'framer-motion'

export const CURRENT_TERMS_VERSION: string = '1.0.0'
export const CURRENT_PRIVACY_POLICY_VERSION: string = '1.0.0'
export const HOST_SERVICE_FEE_STANDARD_PERCENTAGE = 20
export const HOST_SERVICE_FEE_PROMO_PERCENTAGE = 5
export const HOST_SERVICE_FEE_PROMO_MONTHS = 12
export const HOST_SERVICE_FEE_PROMO_ACTIVE = true

// export const RAPIDAPI_HOST_IMPORT_01_45_API: string = 'airbnb45.p.rapidapi.com'

// Used only in admin tools as a fallback host for host-listing lookup
// (same endpoint shape as private API).
// export const RAPIDAPI_HOST_IMPORT_01_65_API: string = 'airbnb65.p.rapidapi.com'

// Used to fetch calendar/price data during enrichment:
// GET /v2/listingPricesFull?id={propertyId}
export const RAPIDAPI_HOST_IMPORT_01_PROPERTIES: string = 'airbnb-listings.p.rapidapi.com'

// Used to fetch listing details during enrichment:
// GET /api/v2/getPropertyDetails?propertyId={propertyId}&currency=EUR
export const RAPIDAPI_HOST_IMPORT_01_19_API: string = 'airbnb19.p.rapidapi.com'

// Primary host used to fetch host-managed listing IDs before enrichment:
// GET /api/profile/get-user-managed-listings?userId={hostId}&offset={offset}&count={count}
export const RAPIDAPI_HOST_IMPORT_01_PRIVATE_API: string = 'airbnb-private-api.p.rapidapi.com'

export const DEFAULT_COMPANY_AVATAR: string = '/others/company-default.svg'

export const PROPERTY_DESCRIPTION_READMORE_LENGTH: number = 475

export const COLORS = {
    light: '#F2F5FF',
    lavender: '#DBDDE6',
    lightRed: '#F39191',
    imperial: '#EF4444',
    dark: '#1F1F1F'
}

// Stripe Configuration
export const STRIPE_CONFIG = {
    publishableKey: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || '',
    secretKey: process.env.STRIPE_SECRET_KEY || '',
    webhookSecret: process.env.STRIPE_WEBHOOK_SECRET || ''
}

// S3-compatible object storage (Railway bucket). All values are server-side only.
// Railway buckets are PRIVATE (no public URL); objects are served via presigned
// URLs through our own routes. We accept either our `S3_*` names or Railway's
// native injected names (ENDPOINT/REGION/ACCESS_KEY_ID/SECRET_ACCESS_KEY/BUCKET)
// so it works whichever the env is configured with.
export const STORAGE_CONFIG = {
    endpoint: process.env.S3_ENDPOINT || process.env.ENDPOINT || '',
    region: process.env.S3_REGION || process.env.REGION || 'auto',
    accessKeyId: process.env.S3_ACCESS_KEY_ID || process.env.ACCESS_KEY_ID || '',
    secretAccessKey: process.env.S3_SECRET_ACCESS_KEY || process.env.SECRET_ACCESS_KEY || '',
    bucket: process.env.S3_BUCKET || process.env.BUCKET || ''
}

const defaultInternalNotificationEmails = 'philipp.barnickel@myworldesk.com'

export const INTERNAL_NOTIFICATION_EMAILS = (
    process.env.INTERNAL_NOTIFICATION_EMAILS || defaultInternalNotificationEmails
)
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean)

export const PAGE_ITEM_LIMIT: number = 9

export const PAGE_TITLE_MAPPING = {
    '/dashboard': 'Home',
    '/chat': 'Chat',
    '/invoices': 'Invoices',
    '/properties': 'Properties'
} as const

export type PAGE_PATH = keyof typeof PAGE_TITLE_MAPPING

export const PAGE_TRANSITION: Transition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.5
}

export const PAGE_VARIANTS = {
    initial: {
        opacity: 0,
        x: '-100vw'
    },
    in: {
        opacity: 1,
        x: 0
    },
    out: {
        opacity: 0,
        x: '100vw'
    }
}

/** For dialogs / overflow regions — avoids x:-100vw clipping scrollports and scrollbar “jumps”. */
export const DIALOG_STEP_VARIANTS = {
    initial: {
        opacity: 0,
        x: -20
    },
    in: {
        opacity: 1,
        x: 0
    },
    out: {
        opacity: 0,
        x: 20
    }
}

export const FEATURE_TYPE_PROPERTY_MAPPING = {
    ACTIVITY_MEDITATION: 'Meditation',

    ACTIVITY_YOGA: 'Yoga',

    ACTIVITY_HIKING: 'Hiking',

    ACTIVITY_SURFING: 'Surfing',

    ACTIVITY_COOKING: 'Cooking',

    SERVICE_CLEANING: 'Cleaning Service',

    SERVICE_BREAKFAST: 'Breakfast Service',

    SERVICE_CATERING: 'Catering Service',

    ALLOWED_PETS: 'Pets Allowed',

    ALLOWED_SMOKING: 'Smoking Allowed',

    KITCHEN: 'Kitchen',

    WASHING_MACHINE: 'Washing Machine',

    DRYER: 'Dryer',

    POOL: 'Pool',

    E_CHARGING: 'Electric Charging',

    WHIRLPOOL: 'Whirlpool',

    FITNESS_STUDIO: 'Fitness Studio',

    GRILL_AREA: 'Grill Area',

    PARKING_COVERED: 'Covered Parking',

    PARKING_UNCOVERED: 'Uncovered Parking',

    OTHER: 'Other'
}

export const FEATURE_TYPE_ROOM_MAPPING = {
    MEET_BIG_MONITOR: 'Big Monitor',

    MEET_PROJECTOR: 'Projector',

    MEET_WHITEBOARD: 'Whiteboard',

    MEET_FLIPCHART: 'Flipchart',

    MEET_CHARGES: 'Charges',

    MEET_LASERPOINTER: 'Laser Pointer',

    MEET_PRESENTER: 'Presenter',

    MEET_DARKENING: 'Darkening',

    MEET_STORAGE: 'Storage',

    MEET_AUDIO_VIDEO_TECH: 'Audio/Video Technology',

    BED_SINGLE: 'Single Bed',

    BED_DOUBLE: 'Double Bed',

    BED_DOUBLE_SMALL: 'Small Double Bed',

    BED_DOUBLE_BIG: 'Big Double Bed',

    BED_BUNK: 'Bunk Bed',

    BED_COUCH: 'Couch Bed',

    BED_FUTON: 'Futon Bed',

    BED_BABY: 'Baby Bed',

    BATH_SHOWER: 'Shower',

    BATH_BATHTUP: 'Bathtub',

    BATH_TOILET: 'Toilet',

    BATH_HAIRDRYER: 'Hairdryer',

    BATH_IRON: 'Iron',

    COFFEE_MACHINE: 'Coffee Machine',

    TV: 'TV',

    BILLIARD_TABLE: 'Billiard Table',

    TABLE_FOOTBALL: 'Table Football',

    CHIMNEY: 'Chimney',

    AIR_CONDITIONER: 'Air Conditioner',

    HEATER: 'Heater',

    PLANTS: 'Plants',

    BALCONY: 'Balcony',

    PATIO: 'Patio',

    BOARD_GAMES_PUZZLES: 'Board Games & Puzzles',

    GAMING_PS5: 'PlayStation 5',

    GAMING_PS4: 'PlayStation 4',

    GAMING_PS3: 'PlayStation 3',

    GAMING_PS2: 'PlayStation 2',

    GAMING_PS1: 'PlayStation 1',

    XBOX1: 'Xbox One',

    XBOX360: 'Xbox 360',

    WII: 'Nintendo Wii',

    NINTENDO: 'Nintendo',

    STREAMING_NETFLIX: 'Netflix',

    STREAMING_AMAZON_PRIME: 'Amazon Prime',

    STREAMING_DISNEY_PLUS: 'Disney Plus'
}

export const FEATURE_TYPE_OFFICE_MAPPING = {
    MONITOR: 'Monitor',

    DOCKING_STATION: 'Docking Station',

    CHAIR: 'Chair',

    CHAIR_ERGONOMICAL: 'Ergonomical Chair',

    TABLE: 'Table',

    TABLE_HEIGHT_ADJUSTABLE: 'Height Adjustable Table',

    TABLE_LAMP: 'Table Lamp',

    KEYBOARD: 'Keyboard',

    MOUSE: 'Mouse',

    MOUSE_PAD: 'Mouse Pad',

    PRINTER: 'Printer',

    SCANNER: 'Scanner',

    HEADPHONES: 'Headphones',

    WRITING_UTENSILS: 'Writing Utensils',

    WEBCAM: 'Webcam',

    HEADSET: 'Headset',

    MICROPHONE: 'Microphone',

    BIN: 'Bin',

    FOOTREST: 'Footrest',

    MASSAGE_SEAT: 'Massage Seat'
}

export function getMenuList(pathname: string): Group[] {
    return [
        {
            groupLabel: '',
            menus: [
                {
                    href: '/dashboard',
                    label: 'Dashboard',
                    active: pathname.includes('/dashboard'),
                    icon: LayoutDashboard,
                    submenus: [],
                    allowedRoles: ['SUPERADMIN', 'HOST_ADMIN', 'GUEST_ADMIN', 'GUEST_TEAM_MEMBER'] as UserRole[]
                },
                {
                    href: '/properties',
                    label: 'Properties',
                    active: pathname.includes('/properties'),
                    icon: House,
                    submenus: [],
                    allowedRoles: ['SUPERADMIN', 'HOST_ADMIN'] as UserRole[]
                },
                {
                    href: '/calendar',
                    label: 'Calendar',
                    active: pathname.includes('/calendar'),
                    icon: CalendarDays,
                    submenus: [],
                    allowedRoles: ['SUPERADMIN', 'HOST_ADMIN'] as UserRole[]
                },
                {
                    href: '/bookings/host',
                    label: 'My Guests',
                    active: pathname.includes('/bookings/host'),
                    icon: CalendarCheck,
                    submenus: [],
                    allowedRoles: ['SUPERADMIN', 'HOST_ADMIN'] as UserRole[]
                },
                {
                    href: '/messages',
                    label: 'Messages',
                    active: pathname.includes('/messages'),
                    icon: MessagesSquare,
                    submenus: [],
                    allowedRoles: ['HOST_ADMIN', 'GUEST_ADMIN'] as UserRole[]
                },
                {
                    href: '/income/host',
                    label: 'Income Overview',
                    active: pathname.includes('/income/host'),
                    icon: DollarSign,
                    submenus: [],
                    allowedRoles: ['SUPERADMIN', 'HOST_ADMIN'] as UserRole[]
                },
                {
                    href: '/home/public',
                    label: 'Search',
                    active: pathname.includes('/home/public'),
                    icon: Search,
                    submenus: [],
                    allowedRoles: ['SUPERADMIN', 'GUEST_ADMIN'] as UserRole[]
                },
                {
                    href: '/bookings/user',
                    label: 'My Bookings',
                    active: pathname.includes('/bookings/user'),
                    icon: Calendar,
                    submenus: [],
                    allowedRoles: ['SUPERADMIN', 'GUEST_ADMIN', 'GUEST_TEAM_MEMBER'] as UserRole[]
                },
                {
                    href: '/favorites',
                    label: 'Favorites',
                    active: pathname.includes('/favorites'),
                    icon: Heart,
                    submenus: [],
                    allowedRoles: ['SUPERADMIN', 'GUEST_ADMIN'] as UserRole[]
                },
                {
                    href: '/notifications',
                    label: 'Notifications',
                    active: pathname.includes('/notifications'),
                    icon: Bell,
                    submenus: [],
                    allowedRoles: ['SUPERADMIN', 'HOST_ADMIN', 'GUEST_ADMIN', 'GUEST_TEAM_MEMBER'] as UserRole[]
                }
            ]
        }
    ]
}
